import { Injectable, inject } from '@angular/core';
import { I18nService } from '../../../core/i18n';

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

export interface FacebookLoginResponse {
  status?: string;
  authResponse?: {
    code?: string;
    accessToken?: string;
    userID?: string;
  };
}

export interface FacebookLoginOptions {
  config_id?: string;
  response_type?: string;
  override_default_response_type?: boolean;
  scope?: string;
  extras?: Record<string, unknown>;
}

export interface FacebookSdk {
  init(params: { appId: string; version: string; xfbml?: boolean; cookie?: boolean }): void;
  login(callback: (response: FacebookLoginResponse) => void, options?: FacebookLoginOptions): void;
}

const SDK_VERSION = 'v23.0';

@Injectable({ providedIn: 'root' })
export class FacebookSdkLoader {
  private readonly i18n = inject(I18nService);
  private loadPromise: Promise<FacebookSdk> | null = null;

  private buildSdkSrc(): string {
    const metaLocale = (this.i18n.locale() ?? 'pt-BR').replace('-', '_');
    return `https://connect.facebook.net/${metaLocale}/sdk.js`;
  }

  load(appId: string): Promise<FacebookSdk> {
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise<FacebookSdk>((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window is not available'));
        return;
      }

      if (window.FB) {
        try {
          window.FB.init({ appId, version: SDK_VERSION, xfbml: false, cookie: true });
        } catch {
          // init can throw if called twice with different appId; ignore and reuse the cached FB
        }
        resolve(window.FB);
        return;
      }

      window.fbAsyncInit = () => {
        if (!window.FB) {
          reject(new Error('Facebook SDK failed to initialize'));
          return;
        }
        window.FB.init({ appId, version: SDK_VERSION, xfbml: false, cookie: true });
        resolve(window.FB);
      };

      const existing = document.getElementById('facebook-jssdk') as HTMLScriptElement | null;
      if (existing) return;

      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = this.buildSdkSrc();
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onerror = () => {
        this.loadPromise = null;
        reject(new Error('Failed to load Facebook SDK'));
      };
      document.head.appendChild(script);
    }).catch((err) => {
      this.loadPromise = null;
      throw err;
    });

    return this.loadPromise;
  }
}
